// Card de uma fila com modelo próprio (ex.: Contratação → "Candidato"):
// etapas, campos do modelo, formulários e andamentos. Sem O.S, QRA nem protocolo.
import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, MenuItem, TextField, Typography, makeStyles,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";

import suporteApi, { mensagemErro } from "./api";
import { Andamentos, dataBR } from "./comum";
import { FormulariosDoCard } from "./Formularios";
import CampoCard from "./CampoCard";

const useStyles = makeStyles((theme) => ({
  etapas: { display: "flex", flexWrap: "wrap", gap: 4, margin: "6px 0 10px" },
  linha: { display: "flex", gap: 8, padding: "5px 0", fontSize: 13, alignItems: "flex-start" },
  rotulo: { color: theme.palette.text.secondary, minWidth: 92, flex: "none" },
  valor: { wordBreak: "break-word", cursor: "pointer", flex: 1 },
  vazio: { color: theme.palette.warning.main, borderBottom: `1px dashed ${theme.palette.warning.main}`, cursor: "pointer" },
  secao: { margin: "4px 0", fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: 0.5 },
}));

const DialogConcluir = ({ card, modelo, open, onClose, onConcluido }) => {
  const finais = (modelo.etapas_finais || []).filter((x) => modelo.etapas.includes(x));
  const opcoes = finais.length ? finais : modelo.etapas;
  const [etapa, setEtapa] = useState("");
  const [obs, setObs] = useState("");
  const [resolver, setResolver] = useState(true);
  const concluir = async () => {
    try {
      const { data } = await suporteApi.post(`/api/atendimentos/${card.id}/concluir`, { etapa, observacao: obs, resolver_whatsapp: resolver });
      toast.success(`${modelo.nome} concluído: ${etapa}`);
      onConcluido(data);
      onClose();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{`Concluir ${modelo.nome.toLowerCase()}`}</DialogTitle>
      <DialogContent>
        <TextField select fullWidth label="Resultado" value={etapa} onChange={(e) => setEtapa(e.target.value)} margin="dense">
          {opcoes.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline rows={2} label="Observação (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} margin="dense" />
        {card.whatsapp_ticket_id && (
          <FormControlLabel control={<Checkbox color="primary" checked={resolver} onChange={(e) => setResolver(e.target.checked)} />}
            label="Resolver a conversa no Whaticket" />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={!etapa} onClick={concluir}>Concluir</Button>
      </DialogActions>
    </Dialog>
  );
};

const CardFila = ({ card, modelo, onAtualizado }) => {
  const c = useStyles();
  const [concluindo, setConcluindo] = useState(false);
  const fechado = card.status === "FECHADO";
  const valores = card.campos || {};

  const salvar = async (corpo) => {
    try {
      const { data } = await suporteApi.patch(`/api/atendimentos/${card.id}/campos`, corpo);
      onAtualizado(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const recarregar = async () => {
    try {
      onAtualizado((await suporteApi.get(`/api/atendimentos/${card.id}`)).data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const reabrir = async () => {
    try {
      onAtualizado((await suporteApi.post(`/api/atendimentos/${card.id}/reabrir-card`)).data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <Chip size="small" color={fechado ? "default" : "primary"} label={fechado ? "Concluído" : "Em andamento"} />
        {card.etapa && <Chip size="small" variant="outlined" color="primary" label={card.etapa} />}
      </div>

      {modelo.etapas.length > 0 && (
        <>
          <Typography className={c.secao} style={{ marginTop: 10 }}>ETAPA</Typography>
          <div className={c.etapas}>
            {modelo.etapas.map((et, i) => {
              const atual = modelo.etapas.indexOf(card.etapa);
              return (
                <Chip key={et} size="small" clickable={!fechado} label={`${i + 1}. ${et}`}
                  color={et === card.etapa ? "primary" : "default"}
                  variant={i < atual ? "default" : et === card.etapa ? "default" : "outlined"}
                  style={i < atual ? { opacity: 0.75 } : undefined}
                  onClick={fechado || et === card.etapa ? undefined : () => salvar({ etapa: et })} />
              );
            })}
          </div>
        </>
      )}

      {(() => {
        // próxima fase que espera o atendente escolher o campo (ex.: modalidade)
        const fl = modelo.fluxo;
        const pendente = fl?.campo && !valores[fl.campo] && !fechado
          && fl.fases.some((f, i) => !(card.fluxo || {})[i] && (f.quando === "abrir" || f.etapa === card.etapa));
        const rot = modelo.campos.find((cp) => cp.id === fl?.campo)?.rotulo;
        return pendente ? (
          <Typography variant="body2" style={{ background: "#fff3e0", color: "#8a4b00", padding: "6px 8px", borderRadius: 6, margin: "6px 0" }}>
            {`Escolha ${rot ? `a ${rot.toLowerCase()}` : "o campo"} para enviar o formulário da próxima fase.`}
          </Typography>
        ) : null;
      })()}
      <Typography className={c.secao}>DADOS</Typography>
      {modelo.campos.map((cp) => (
        <CampoCard key={cp.id} campo={cp} valor={valores[cp.id]} opcoes={modelo.opcoes?.[cp.lista]}
          bloqueado={fechado} onSalvar={(id, v) => salvar({ campos: { [id]: v } })} />
      ))}
      <div className={c.linha}>
        <span className={c.rotulo}>Aberto</span>
        <span>{`${card.analista || "-"} em ${dataBR(card.inicio)}`}</span>
      </div>
      {fechado && (
        <div className={c.linha}>
          <span className={c.rotulo}>Concluído</span>
          <span>{`${card.analista_fim || "-"} em ${dataBR(card.fim)}`}{card.observacao ? ` — ${card.observacao}` : ""}</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
        {fechado
          ? <Button size="small" variant="outlined" onClick={reabrir}>Reabrir</Button>
          : <Button size="small" variant="contained" color="primary" startIcon={<CheckIcon />} onClick={() => setConcluindo(true)}>Concluir</Button>}
      </div>

      <FormulariosDoCard card={card} onCardMudou={recarregar} />
      <Divider style={{ margin: "10px 0" }} />
      <Typography className={c.secao}>ANDAMENTO</Typography>
      <Andamentos card={card} onAtualizado={onAtualizado} />
      {concluindo && (
        <DialogConcluir card={card} modelo={modelo} open onClose={() => setConcluindo(false)} onConcluido={onAtualizado} />
      )}
    </div>
  );
};

export default CardFila;
